import { Geometry } from '@pacem/pacem-numerical';
import { isPerspectiveCamera, isOrthographicCamera } from '../../types';
//namespace Pacem.Drawing3D.WebGPU {
const Vector3D = Geometry.LinearAlgebra.Vector3D;
const Matrix3D = Geometry.LinearAlgebra.Matrix3D;
const DEFAULT_SIZE = { width: 1, height: 1 };
export class Cameras {
    /**
     * Creates a new webgpu {@link Camera} given a canonic one.
     * @param camera
     * @returns
     */
    static create(camera) {
        if (isPerspectiveCamera(camera)) {
            return new PerspectiveCamera(camera);
        }
        if (isOrthographicCamera(camera)) {
            return new OrthographicCamera(camera);
        }
        throw new TypeError(`Camera type '${camera['type']}' not supported.`);
    }
}
/**
 * Camera orientation 4x4 matrix.
 * @param eye
 * @param target
 * @param up
 * @returns
 */
function view(eye, target, up) {
    const z = /* inverse forward is eye -> target */ Vector3D.subtract(target, eye);
    const x = Vector3D.cross(up, z);
    const y = Vector3D.cross(z, x);
    Vector3D.normalize(x);
    Vector3D.normalize(z);
    Vector3D.normalize(y);
    const offsetX = -Vector3D.dot(x, eye);
    const offsetY = -Vector3D.dot(y, eye);
    const offsetZ = -Vector3D.dot(z, eye);
    return Matrix3D.from(x.x, y.x, z.x, 0, x.y, y.y, z.y, 0, x.z, y.z, z.z, 0, offsetX, offsetY, offsetZ, 1);
}
function assertNearFarCorrectness(near, far) {
    if (near <= 0) {
        throw new RangeError("Near plane distance must be positive.");
    }
    if (far <= near) {
        throw new RangeError("Far plane distance must be greater than near plane distance.");
    }
}
/**
 * Perspective transformation 4x4 matrix.
 * @param fovY Vertical field of view (in radians)
 * @param aspectRatio Frustum aspect ratio
 * @param near Clipping plane near distance (not z coordinate)
 * @param far Clipping plane far distance (not z coordinate)
 * @returns
 */
function perspective(fovY = 120, aspectRatio = 1, near = 0.1, far = 1000) {
    assertNearFarCorrectness(near, far);
    const scale = Math.tan(.5 * Math.PI * (1 - fovY / 180));
    const m11 = scale / aspectRatio;
    const finitelyFar = Number.isFinite(far);
    const m33 = finitelyFar ? (far / (near - far)) : -1;
    const offsetZ = finitelyFar ? (far * near / (near - far)) : -near;
    return Matrix3D.from(m11, 0, 0, 0, 0, scale, 0, 0, 0, 0, m33, -1, 0, 0, offsetZ, 0);
}
/**
 * Othographic transformation 4x4 matrix.
 * @param left Viewport's left side
 * @param right Viewport's right side
 * @param top Viewport's top side
 * @param bottom Viewport's bottom side
 * @param near Clipping plane near distance
 * @param far Clipping plane far distance
 */
function orthographic(left, right, top, bottom, near = .1, far = 1000) {
    assertNearFarCorrectness(near, far);
    return Matrix3D.from(2 / (right - left), 0, 0, 0, 0, 2 / (top - bottom), 0, 0, 0, 0, 1 / (near - far), 0, (left + right) / (left - right), (top + bottom) / (bottom - top), near / (near - far), 1);
}
export class OrthographicCamera {
    constructor(_camera) {
        this._camera = _camera;
    }
    static view(camera) {
        return view(camera.position, camera.lookAt, camera.up);
    }
    static projection(camera, size = DEFAULT_SIZE) {
        const eye = camera.position, target = camera.lookAt;
        const z = Vector3D.subtract(target, eye);
        Vector3D.normalize(z);
        const zoom = Vector3D.dot(z, eye);
        const { height, width } = size;
        const ratio = width / height;
        const factor = .5 * zoom;
        const w = factor * ratio, h = factor;
        const top = camera.top * h, bottom = camera.bottom * h;
        const right = camera.right * w, left = camera.left * w;
        return orthographic(left, right, top, bottom, camera.near, camera.far);
    }
    view() {
        return OrthographicCamera.view(this._camera);
    }
    projection(size) {
        return OrthographicCamera.projection(this._camera, size);
    }
}
export class PerspectiveCamera {
    constructor(_camera) {
        this._camera = _camera;
    }
    static view(camera) {
        return view(camera.position, camera.lookAt, camera.up);
    }
    static projection(camera, size = DEFAULT_SIZE) {
        const aspectRatio = (size.width / size.height) / camera.aspectRatio;
        return perspective(camera.fov, aspectRatio, camera.near, camera.far);
    }
    view() {
        return PerspectiveCamera.view(this._camera);
    }
    projection(size) {
        return PerspectiveCamera.projection(this._camera, size);
    }
}
